import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { SenderConfirmModal } from "../components/SenderConfirmModal";

import { listOtherSenders, OtherSender, SenderIdentity } from "./senderConfirmation.util";

interface SenderConfirmGate {
	/**
	 * Resolves `true` when the messages may go out: every sender is the current user, or the user
	 * confirmed sending as the others. `false` on Cancel — nothing may be sent then, and the drafts
	 * stay as they are. A second call while one is open (Send pressed again) answers `false` and
	 * leaves the open question alone. `messageCount` (default: one per sender) picks "this message"
	 * or "these messages".
	 */
	confirmSenders: (
		senders: (SenderIdentity | undefined)[],
		messageCount?: number
	) => Promise<boolean>;
	/** Render this wherever the caller renders its own modals. */
	senderConfirmModal: ReactNode;
}

interface Prompt {
	others: OtherSender[];
	messageCount: number;
}

/**
 * The composer's question before a message goes out as someone else (PREC-4312). Every send path
 * of the chat composer — Inbox (before the undo window starts), Review to send, Compose, Outbox,
 * the lead panel and the extension's lead panel — asks through `useAddInboxChat`'s Send, which
 * asks here once per batch. Who counts as "someone else" is `isSomeoneElse`.
 */
export const useSenderConfirmGate = (): SenderConfirmGate => {
	const { user } = useAppSelector(authSelector);
	const { data: sendingUsers } = useGetSendersUsersQuery();
	const [prompt, setPrompt] = useState<Prompt | null>(null);
	const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);

	// A prompt left open when the composer goes away sends nothing.
	useEffect(() => () => resolveRef.current?.(false), []);

	const confirmSenders = useCallback(
		(senders: (SenderIdentity | undefined)[], messageCount = senders.length) => {
			const others = listOtherSenders(senders, user?.id, sendingUsers?.data);

			if (others.length === 0) {
				return Promise.resolve(true);
			}

			if (resolveRef.current) {
				return Promise.resolve(false);
			}

			return new Promise<boolean>((resolve) => {
				resolveRef.current = resolve;
				setPrompt({ others, messageCount });
			});
		},
		[user?.id, sendingUsers?.data]
	);

	const answer = (confirmed: boolean) => {
		resolveRef.current?.(confirmed);
		resolveRef.current = null;
		setPrompt(null);
	};

	const senderConfirmModal = prompt ? (
		<SenderConfirmModal
			messageCount={prompt.messageCount}
			others={prompt.others}
			onCancel={() => answer(false)}
			onConfirm={() => answer(true)}
		/>
	) : null;

	return { confirmSenders, senderConfirmModal };
};
